import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './button';
import { cn } from '../../lib/utils';

/**
 * Mobile-friendly pagination control.
 *  - Mobile: Prev / "Page X of Y" / Next.
 *  - Desktop (sm+): numbered pages with a sliding window + prev/next.
 * Replaces the per-page hand-rolled pagination across the admin.
 */
export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Optional "Showing X–Y of Z" summary. */
  summary?: string;
  className?: string;
}

function pageWindow(current: number, total: number): number[] {
  const span = 5;
  let start = Math.max(1, current - Math.floor(span / 2));
  let end = Math.min(total, start + span - 1);
  start = Math.max(1, Math.min(start, end - span + 1));
  const out: number[] = [];
  for (let p = start; p <= end; p++) out.push(p);
  return out;
}

export function Pagination({ currentPage, totalPages, onPageChange, summary, className }: PaginationProps) {
  if (totalPages <= 1) {
    return summary ? (
      <div className={cn('text-sm text-muted-foreground', className)}>{summary}</div>
    ) : null;
  }

  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;
  const go = (p: number) => onPageChange(Math.min(totalPages, Math.max(1, p)));

  return (
    <div className={cn('flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between', className)}>
      {summary && <div className="text-sm text-muted-foreground order-2 sm:order-1">{summary}</div>}

      <div className="flex items-center justify-between gap-2 order-1 sm:order-2">
        <Button variant="outline" size="sm" onClick={() => go(currentPage - 1)} disabled={!canPrev}>
          <ChevronLeft className="h-4 w-4" />
          <span className="ml-1 hidden sm:inline">Prev</span>
        </Button>

        {/* Mobile: compact indicator */}
        <span className="text-sm font-medium sm:hidden">
          Page {currentPage} of {totalPages}
        </span>

        {/* Desktop: numbered pages */}
        <div className="hidden items-center gap-1 sm:flex">
          {pageWindow(currentPage, totalPages)[0] > 1 && (
            <>
              <Button variant={currentPage === 1 ? 'default' : 'outline'} size="sm" onClick={() => go(1)}>1</Button>
              <span className="px-1 text-muted-foreground">…</span>
            </>
          )}
          {pageWindow(currentPage, totalPages).map((p) => (
            <Button key={p} variant={p === currentPage ? 'default' : 'outline'} size="sm" onClick={() => go(p)}>
              {p}
            </Button>
          ))}
          {pageWindow(currentPage, totalPages).slice(-1)[0] < totalPages && (
            <>
              <span className="px-1 text-muted-foreground">…</span>
              <Button variant={currentPage === totalPages ? 'default' : 'outline'} size="sm" onClick={() => go(totalPages)}>
                {totalPages}
              </Button>
            </>
          )}
        </div>

        <Button variant="outline" size="sm" onClick={() => go(currentPage + 1)} disabled={!canNext}>
          <span className="mr-1 hidden sm:inline">Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default Pagination;
